// TanStack Query hooks for tenant management.
//
// Conventions:
//   - Query keys: ['tenants', 'list', params] / ['tenants', 'detail', slug]
//   - Mutations invalidate the list and the touched detail.
//   - Detail query auto-polls while status='provisioning' (refetchInterval),
//     stops once active — the create flow is async on the server.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi, ListTenantsParams } from './api';
import type { CreateTenantInput, Tenant } from '../../api/types';

const tenantsKeys = {
  all: ['tenants'] as const,
  list: (params?: ListTenantsParams) => ['tenants', 'list', params ?? {}] as const,
  detail: (slug: string) => ['tenants', 'detail', slug] as const,
};

export function useTenants(params?: ListTenantsParams) {
  return useQuery({
    queryKey: tenantsKeys.list(params),
    queryFn: () => tenantsApi.list(params),
  });
}

export function useTenant(slug: string | undefined) {
  return useQuery({
    queryKey: tenantsKeys.detail(slug ?? ''),
    queryFn: () => tenantsApi.get(slug as string),
    enabled: !!slug,
    // Poll every 2s while the tenant is provisioning; stop once active.
    refetchInterval: (q) => (q.state.data?.status === 'provisioning' ? 2000 : false),
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation<Tenant, Error, CreateTenantInput>({
    mutationFn: (input) => tenantsApi.create(input),
    onSuccess: (created) => {
      // Invalidate list so the new row shows immediately, and seed the
      // detail cache so a navigation to the just-created tenant has
      // first-paint data without an extra request.
      qc.invalidateQueries({ queryKey: tenantsKeys.all });
      qc.setQueryData(tenantsKeys.detail(created.slug), created);
    },
  });
}
